import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { BehaviorSubject, Subject, lastValueFrom } from 'rxjs'
import { environment } from '../../environments/environment'
import { AuthenticationResponse } from '../models/AuthenticationResponse'
import { Authentication } from '../models/Authentication'
import { ResponseStatus } from '../models/ResponseStatus'

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  public userInfo = new BehaviorSubject<Authentication | undefined>(undefined)

  private generateAlphanumericId(length: number): string {
    const chars =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    let result = ''
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    return result
  }

  public get deviceId(): string {
    const savedDeviceId = localStorage.getItem('deviceId')
    if (savedDeviceId) {
      return savedDeviceId
    }
    const deviceId = this.generateAlphanumericId(20)
    localStorage.setItem('deviceId', deviceId)
    return deviceId
  }

  constructor(private http: HttpClient) {}

  async login(
    username: string,
    password: string,
    deviceId: string,
  ): Promise<{ success: boolean; errorCode?: string; errorMessage?: string }> {
    const url = `${environment.apiUrl}/auth/login`

    const body = {
      username,
      password,
      deviceId,
    }

    const resp = this.http.post<AuthenticationResponse>(url, body)
    const result = await lastValueFrom<AuthenticationResponse>(resp).catch(
      (err) => {
        return {
          status: false,
          errorCode: err.statusText,
          errorDescription: err.message,
        } as AuthenticationResponse
      },
    )

    this.userInfo.next({
      name: result?.name,
      surname: result?.surname,
      email: result?.email,
      restaurantId: result?.restaurantId,
      restaurant: result?.restaurant,
      groupId: result?.groupId,
    })
    if (result.status) {
      return { success: true }
    }
    return {
      success: false,
      errorCode: result.errorCode,
      errorMessage: result.errorDescription,
    }
  }

  async loginWithoutCredentials(
  employeeId: string,
  restaurantId: string,
  deviceId: string,
  workingDate?: string,
): Promise<{ success: boolean; errorCode?: string; errorMessage?: string }> {
  const url = `${environment.apiUrl}/auth/login-with-employee`

  const body = {
    employeeId,
    restaurantId,
    deviceId,
    workingDate,
  }

  const resp = this.http.post<AuthenticationResponse>(url, body)

  const result = await lastValueFrom<AuthenticationResponse>(resp).catch(
    (err) => {
      return {
        status: false,
        errorCode: err.statusText,
        errorDescription: err.message,
      } as AuthenticationResponse
    },
  )

  this.userInfo.next({
    name: result?.name,
    surname: result?.surname,
    email: result?.email,
    restaurantId: result?.restaurantId,
    restaurant: result?.restaurant,
    groupId: result?.groupId,
  })

  if (result.status) {
    return { success: true }
  }

  return {
    success: false,
    errorCode: result.errorCode,
    errorMessage: result.errorDescription,
  }
}

  async isAuthenticated(): Promise<boolean> {
    const url = `${environment.apiUrl}/auth/is_authenticated`
    const resp = this.http.get<AuthenticationResponse>(url)
    const result = await lastValueFrom<AuthenticationResponse>(resp).catch(
      (err) => {
        return {
          status: false,
          errorCode: err.statusText,
          errorDescription: err.message,
        } as AuthenticationResponse
      },
    )

    this.userInfo.next({
      name: result.name,
      surname: result.surname,
      email: result.email,
      restaurantId: result.restaurantId,
      restaurant: result.restaurant,
      groupId: result.groupId,
    })

    return result.status
  }

  async logout(): Promise<void> {
    const url = `${environment.apiUrl}/auth/logout`
    const resp = this.http.post<ResponseStatus>(url, {})
    const result = await lastValueFrom<ResponseStatus>(resp).catch((err) => {
      return {
        status: false,
        errorCode: err.statusText,
        errorDescription: err.message,
      } as AuthenticationResponse
    })
    if (result.status) {
      this.userInfo.next({})
    }
  }
}
